import { useEffect, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';
import {
  ERROR_CODES,
  SALES_INQUIRY_STATUSES,
  type PanelSalesInquiryDetailDto,
  type PanelSalesInquiryHistoryEntryDto,
  type SalesInquiryStatus,
} from '@icebreaker/shared';
import {
  Avatar,
  Button,
  Modal,
  SectionHeading,
  Select,
  Skeleton,
  Tag,
  useErrorMessage,
} from '@icebreaker/ui';
import { isOutcomeUnknown } from '@/features/organizations/model/write-outcome';
import { CreateOrganizationDialog } from '@/features/organizations/ui/create-organization-dialog';
import { GrantCreditsDialog } from '@/features/organizations/ui/grant-credits-dialog';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import type { RootState } from '@/shared/store/store';
import {
  calendarDayInstant,
  numericDateOptions,
  numericDateTimeOptions,
  SALES_STATUS_VARIANT,
  inquiryKindLabel,
} from '../model/sales-view';
import {
  changeSalesInquiryStatus,
  fetchSalesInquiry,
  salesDetailClosed,
} from '../store/sales-inquiries.slice';
import { ReferralCodeValue } from './referral-code-value';
import styles from './sales.module.css';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * SATIŞ ADAYI DETAYI (P2) — sağdan açılan kenar katmanı, liste arkada soluk.
 *
 * Açılış kişisel veriye erişimdir: sunucu her okumayı denetim defterine
 * yazar ve geçmişte "detayı görüntüledi" satırı olarak döner. Bu yüzden
 * detay yalnız bu katman açıldığında İSTENİR.
 *
 * Anonimleşmiş başvuruda (12 ay, K8) kişisel alanlar "anonimleştirildi" yazar,
 * avatar yerine nötr kare çizilir, istatistik alanları görünür kalır ve
 * "Bu adaydan kurum aç" çizilmez.
 */
export function SalesInquiryDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const t = useTranslation();
  const errorMessage = useErrorMessage();
  const dispatch = useAppDispatch();
  const detail = useAppSelector((root: RootState) => root.panelSales.detail);
  const statusChange = useAppSelector((root: RootState) => root.panelSales.statusChange);
  const [creating, setCreating] = useState(false);
  const [granting, setGranting] = useState(false);
  const [lastRequested, setLastRequested] = useState<SalesInquiryStatus | null>(null);
  const valid = UUID.test(id);

  useEffect(() => {
    if (valid) dispatch(fetchSalesInquiry.request({ id }));
    return () => {
      dispatch(salesDetailClosed());
    };
  }, [dispatch, id, valid]);

  const data = detail.id === id ? detail.data : null;
  const notFound =
    !valid ||
    (detail.phase === 'failed' &&
      (detail.error?.code === ERROR_CODES.NOT_FOUND || detail.error?.statusCode === 404));
  const saving = statusChange.phase === 'loading';

  const changeStatus = (status: SalesInquiryStatus) => {
    setLastRequested(status);
    dispatch(changeSalesInquiryStatus.request({ id, status }));
  };

  const header = (
    <header className={styles.detailHead}>
      <div className={styles.detailHeadText}>
        {data ? (
          <>
            <p className={styles.kicker}>
              {t.t('admin.sales.detail.kicker', {
                date: t.formatDate(data.createdAt, { dateStyle: 'medium' }),
              })}
            </p>
            <h2 className={styles.detailTitle}>
              {data.companyName ?? t.t('admin.sales.anonymized')}
            </h2>
            <div className={styles.tagRow}>
              <Tag variant="interest">{inquiryKindLabel(t, data)}</Tag>
              <Tag variant={SALES_STATUS_VARIANT[data.status]}>
                {t.t(`admin.sales.statuses.${data.status}`)}
              </Tag>
            </div>
          </>
        ) : (
          <h2 className={styles.detailTitle}>{t.t('admin.sales.detail.label')}</h2>
        )}
      </div>
      <Button
        variant="ghost"
        size="sm"
        className={styles.closeButton}
        aria-label={t.t('admin.sales.detail.close')}
        disabled={saving}
        onClick={onClose}
      >
        <X size={18} aria-hidden="true" />
      </Button>
    </header>
  );

  const canCreate = data !== null && !data.anonymized && data.organization === null;
  /*
   * KREDİ TALEBİNDEN DOĞRUDAN YÜKLEME (12.10): talebi açan kurum bellidir,
   * personel kurum detayına gitmeden aynı pencereyi buradan açar.
   */
  const grantOrganization =
    data !== null && !data.anonymized && data.topic === 'credit_topup' ? data.organization : null;

  return (
    <>
      <Modal
        open
        placement="side-wide"
        title={data?.companyName ?? t.t('admin.sales.detail.label')}
        closeLabel={t.t('admin.sales.detail.close')}
        header={header}
        // Kayıt sürerken ve üstte "Kurum aç" açıkken Esc ile KAPANMAZ (T4 QA P3-2).
        onClose={onClose}
        closeDisabled={saving || creating || granting}
        footer={
          data ? (
            <div className={styles.actionBar}>
              <label className={styles.statusPicker}>
                <span className={styles.statusLabel}>{t.t('admin.sales.detail.statusLabel')}</span>
                <Select
                  value={data.status}
                  disabled={saving}
                  aria-busy={saving || undefined}
                  onChange={(event) => changeStatus(event.target.value as SalesInquiryStatus)}
                >
                  {SALES_INQUIRY_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {t.t(`admin.sales.statuses.${status}`)}
                    </option>
                  ))}
                </Select>
              </label>
              {canCreate ? (
                <Button variant="primary" onClick={() => setCreating(true)}>
                  {t.t('admin.sales.detail.openOrganization')}
                </Button>
              ) : null}
              {grantOrganization ? (
                <Button variant="primary" onClick={() => setGranting(true)}>
                  {t.t('admin.credit.grant')}
                </Button>
              ) : null}
            </div>
          ) : undefined
        }
      >
        {notFound ? (
          <div className={styles.detailState} role="status">
            <p className={[styles.banner, styles.bannerMuted].join(' ')}>
              {t.t('admin.sales.detail.notFound')}
            </p>
            <Button variant="primary" onClick={onClose}>
              {t.t('admin.sales.detail.backToList')}
            </Button>
          </div>
        ) : detail.phase === 'failed' && !data ? (
          <div className={styles.detailState} role="alert">
            <p className={[styles.banner, styles.bannerError].join(' ')}>
              {/* İz yazılamadıysa veri bilerek gelmedi (C21): sebep söylenir. */}
              {detail.error?.code === ERROR_CODES.AUDIT_UNAVAILABLE
                ? errorMessage(detail.error)
                : t.t('admin.sales.detail.loadError')}
            </p>
            <Button variant="secondary" onClick={() => dispatch(fetchSalesInquiry.request({ id }))}>
              {t.t('admin.sales.retry')}
            </Button>
          </div>
        ) : !data ? (
          <Skeleton label={t.t('admin.sales.detail.loading')} lines={8} />
        ) : (
          <DetailBody
            data={data}
            saving={saving}
            saveFailed={statusChange.phase === 'failed'}
            saveUnknown={isOutcomeUnknown(statusChange.error)}
            restored={statusChange.restored}
            onRetry={lastRequested ? () => changeStatus(lastRequested) : undefined}
          />
        )}
      </Modal>
      {creating && data ? (
        <CreateOrganizationDialog
          prefill={{
            name: data.companyName ?? '',
            ownerEmail: data.email ?? '',
            lead: { id: data.id, name: data.companyName ?? '', createdAt: data.createdAt },
          }}
          onClose={() => setCreating(false)}
        />
      ) : null}
      {granting && grantOrganization ? (
        <GrantCreditsDialog
          organizationId={grantOrganization.id}
          organizationName={grantOrganization.name}
          lead={data}
          onClose={() => setGranting(false)}
        />
      ) : null}
    </>
  );
}

function DetailBody({
  data,
  saving,
  saveFailed,
  saveUnknown,
  restored,
  onRetry,
}: {
  data: PanelSalesInquiryDetailDto;
  saving: boolean;
  saveFailed: boolean;
  /** Yanıt gelmedi: durum sunucuda değişmiş olabilir, "geri yüklendi" denmez. */
  saveUnknown: boolean;
  restored: SalesInquiryStatus | null;
  onRetry: (() => void) | undefined;
}) {
  const t = useTranslation();
  const anonymized = t.t('admin.sales.anonymized');
  const notProvided = t.t('admin.sales.detail.notProvided');
  const dateTime = numericDateTimeOptions(t.locale);

  const rows: readonly {
    key: string;
    value: ReactNode;
    copy?: string | null;
    mono?: boolean;
    muted?: boolean;
  }[] = [
    {
      key: t.t('admin.sales.detail.fields.company'),
      value: data.companyName ?? anonymized,
      muted: data.companyName === null,
    },
    {
      key: t.t('admin.sales.detail.fields.eventType'),
      value: inquiryKindLabel(t, data),
    },
    {
      key: t.t('admin.sales.detail.fields.attendees'),
      value:
        data.estimatedAttendees !== null
          ? t.formatNumber(data.estimatedAttendees)
          : (data.attendeeBand ?? notProvided),
    },
    {
      key: t.t('admin.sales.detail.fields.estimatedDate'),
      value: data.estimatedDate
        ? t.formatDate(calendarDayInstant(data.estimatedDate), { month: 'long', year: 'numeric' })
        : data.anonymized
          ? anonymized
          : notProvided,
      muted: data.estimatedDate === null,
    },
    {
      key: t.t('admin.sales.detail.fields.email'),
      value: data.email ?? anonymized,
      copy: data.email,
      mono: data.email !== null,
      muted: data.email === null,
    },
    {
      key: t.t('admin.sales.detail.fields.phone'),
      value: data.phone ?? (data.anonymized ? anonymized : notProvided),
      copy: data.phone,
      mono: data.phone !== null,
      muted: data.phone === null,
    },
    {
      key: t.t('admin.sales.detail.fields.note'),
      value: data.note ?? (data.anonymized ? anonymized : notProvided),
      muted: data.note === null,
    },
    {
      key: t.t('admin.sales.detail.fields.locale'),
      value: data.locale
        ? t.t(`language.${data.locale}`)
        : data.anonymized
          ? anonymized
          : notProvided,
      muted: data.locale === null,
    },
    {
      key: t.t('admin.sales.detail.fields.createdAt'),
      value: t.formatDate(data.createdAt, dateTime),
    },
    {
      key: t.t('admin.sales.detail.fields.linkedUser'),
      value: data.linkedUser
        ? t.t('admin.sales.detail.linkedYes', {
            name: data.linkedUser.displayName ?? t.t('admin.sales.detail.removedAccount'),
          })
        : t.t('admin.sales.detail.linkedNo'),
    },
    {
      key: t.t('admin.sales.detail.fields.notice'),
      value: t.t('admin.sales.detail.noticeValue', {
        version: data.noticeVersion,
        date: t.formatDate(data.noticeAcknowledgedAt, dateTime),
      }),
    },
    {
      key: t.t('admin.sales.detail.fields.organization'),
      value: data.organization?.name ?? t.t('admin.sales.detail.organizationNone'),
    },
    // Kod alanı yalnız kredi talebi formunda var (Story 2): başka konuda satır çizilmez.
    ...(data.topic === 'credit_topup'
      ? [
          {
            key: t.t('admin.sales.detail.fields.referralCode'),
            value: <ReferralCodeValue data={data} />,
            muted: data.referralCode === null,
          },
        ]
      : []),
  ];

  return (
    <>
      {data.anonymized && data.anonymizedAt ? (
        <p className={[styles.banner, styles.bannerMuted].join(' ')} role="status">
          {t.t('admin.sales.detail.anonymizedBanner', {
            date: t.formatDate(data.anonymizedAt, numericDateOptions(t.locale)),
          })}
        </p>
      ) : null}
      {saving ? (
        <p className={[styles.banner, styles.bannerInfo].join(' ')} role="status">
          {t.t('admin.sales.detail.saving')}
        </p>
      ) : null}
      {saveFailed && (restored || saveUnknown) ? (
        <div
          className={[styles.banner, styles.bannerError].join(' ')}
          role="alert"
          data-outcome={saveUnknown || !restored ? 'unknown' : 'rejected'}
        >
          {saveUnknown || !restored
            ? `${t.t('admin.write.unknown')} ${t.t('admin.write.reloadedStatus')}`
            : t.t('admin.sales.detail.saveError', {
                status: t.t(`admin.sales.statuses.${restored}`),
              })}
          {onRetry ? (
            <div className={styles.bannerAction}>
              <Button variant="secondary" size="sm" onClick={onRetry}>
                {t.t('admin.sales.retry')}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className={styles.contact}>
        {data.contactName ? (
          <Avatar name={data.contactName} size="lg" />
        ) : (
          <span
            className={[styles.neutralMark, styles.neutralMarkLarge].join(' ')}
            aria-hidden="true"
          >
            —
          </span>
        )}
        <span className={styles.contactText}>
          <span className={[styles.contactName, data.contactName ? '' : styles.muted].join(' ')}>
            {data.contactName ?? anonymized}
          </span>
          <span className={styles.contactRole}>
            {data.linkedUser
              ? t.t('admin.sales.detail.contactLinked')
              : t.t('admin.sales.detail.contactRole')}
          </span>
        </span>
      </div>

      <dl className={styles.fields}>
        {rows.map((row) => (
          <div key={row.key} className={styles.fieldRow}>
            <dt className={styles.fieldKey}>{row.key}</dt>
            <dd
              className={[
                styles.fieldValue,
                row.mono ? styles.mono : '',
                row.muted ? styles.notProvided : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {row.value}
            </dd>
            {row.copy ? <CopyButton value={row.copy} /> : <span />}
          </div>
        ))}
      </dl>

      <section className={styles.history} aria-labelledby={`history-${data.id}`}>
        <SectionHeading
          titleId={`history-${data.id}`}
          title={t.t('admin.sales.detail.historyTitle')}
        />
        <ul className={styles.historyList}>
          {data.history.map((entry, index) => (
            <HistoryItem key={`${entry.at}-${index}`} entry={entry} />
          ))}
          <li className={styles.historyItem}>
            <span className={styles.neutralMark} aria-hidden="true">
              —
            </span>
            <span className={styles.historyText}>
              <span className={styles.historyWho}>{t.t('admin.sales.detail.system')}</span>{' '}
              {t.t('admin.sales.detail.historyCreated', {
                locale: data.locale ? t.t(`language.${data.locale}`) : '—',
              })}
              <span className={styles.historyWhen}>{t.formatDate(data.createdAt, dateTime)}</span>
            </span>
          </li>
        </ul>
      </section>
    </>
  );
}

function HistoryItem({ entry }: { entry: PanelSalesInquiryHistoryEntryDto }) {
  const t = useTranslation();
  const name = entry.staff.displayName ?? t.t('admin.sales.detail.removedAccount');
  const what =
    entry.kind === 'viewed'
      ? t.t('admin.sales.detail.historyViewed')
      : t.t('admin.sales.detail.historyStatus', {
          from: entry.fromStatus ? t.t(`admin.sales.statuses.${entry.fromStatus}`) : '—',
          to: entry.toStatus ? t.t(`admin.sales.statuses.${entry.toStatus}`) : '—',
        });
  return (
    <li className={styles.historyItem}>
      <Avatar name={name} size="xs" />
      <span className={styles.historyText}>
        <span className={styles.historyWho}>{name}</span> {what}
        <span className={styles.historyWhen}>
          {t.formatDate(entry.at, numericDateTimeOptions(t.locale))}
        </span>
      </span>
    </li>
  );
}

/** "Kopyala" — yalnız değer varken çizilir (referans P2). */
function CopyButton({ value }: { value: string }) {
  const t = useTranslation();
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return undefined;
    const handle = window.setTimeout(() => setCopied(false), 1600);
    return () => window.clearTimeout(handle);
  }, [copied]);
  return (
    <Button
      variant="secondary"
      size="sm"
      onClick={() => {
        void navigator.clipboard?.writeText(value).then(() => setCopied(true));
      }}
    >
      <span aria-live="polite">
        {copied ? t.t('admin.sales.detail.copied') : t.t('admin.sales.detail.copy')}
      </span>
    </Button>
  );
}
